<template>
    <div class="flex group w-fit min-w-80 max-w-[85%] items-end gap-2 animate-fadeIn">
        
        <!-- Avatar avec effet moderne -->
        <div v-if="!selfMessage" class="flex-shrink-0 mb-1 relative">
            <div class="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full blur-lg opacity-0 group-hover:opacity-30 transition-all duration-500"></div>
            <div class="relative">
                <img 
                    v-if="conversation?.profile" 
                    :src="conversation.profile" 
                    alt="Avatar"
                    class="w-10 h-10 rounded-full object-cover border-2 border-white dark:border-gray-700 shadow-xl ring-2 ring-blue-100 dark:ring-blue-900/30 transition-transform duration-300 group-hover:scale-110"
                />
                <div 
                    v-else
                    class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border-2 border-white dark:border-gray-700 shadow-xl ring-2 ring-blue-100 dark:ring-blue-900/30 transition-transform duration-300 group-hover:scale-110"
                >
                    <i class="las la-user text-white text-lg"></i>
                </div>
                <!-- Status badge -->
                <div class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full"></div>
            </div>
        </div>

        <!-- Contenu principal -->
        <div class="flex-1 min-w-0 space-y-1">
            
            <!-- Header avec métadonnées -->
            <div class="flex items-center justify-between gap-3 px-1">
                <!-- Badge transféré avec animation -->
                <transition name="slide-fade">
                    <div 
                        v-if="message.isForwarded" 
                        class="flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-700 rounded-full shadow-sm"
                    >
                        <i class="fas fa-share text-xs text-blue-600 dark:text-blue-400 animate-pulse"></i>
                        <span class="text-xs font-semibold text-blue-700 dark:text-blue-300">Transféré</span>
                    </div>
                </transition>
                
                <!-- Agent name avec badge -->
                <div 
                    v-if="message?.agent?.firstname"
                    class="flex items-center gap-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-full ml-auto"
                >
                    <div class="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span class="text-xs font-bold text-gray-700 dark:text-gray-300">
                        {{ message.agent.firstname }}
                    </span>
                </div>
            </div>

            <!-- Message cité avec nouveau design -->
            <transition name="slide-down">
                <div 
                    v-if="message.parent_wp_id" 
                    class="mx-1 group/quote cursor-pointer"
                >
                    <div class="relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-3 border-l-4 border-blue-500 dark:border-blue-400 shadow-md hover:shadow-lg transition-all duration-300">
                        <!-- Effet de brillance au hover -->
                        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover/quote:translate-x-[100%] transition-transform duration-1000"></div>
                        
                        <div class="relative flex items-start gap-2.5">
                            <div class="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                <i class="las la-reply text-blue-600 dark:text-blue-400"></i>
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="text-xs font-medium text-blue-600 dark:text-blue-400 mb-0.5">
                                    En réponse à
                                </p>
                                <p class="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 leading-relaxed">
                                    Message précédent cité ici...
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <!-- Bulle audio ultra moderne -->
            <div 
                :class="[
                    'relative rounded-3xl p-4 shadow-2xl backdrop-blur-sm transition-all duration-300 group-hover:shadow-3xl',
                    selfMessage 
                        ? 'bg-gradient-to-br from-teal-500 via-teal-600 to-emerald-600 dark:from-teal-600 dark:via-teal-700 dark:to-emerald-700 text-white ml-auto transform hover:scale-[1.02]' 
                        : 'bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-850 text-gray-900 dark:text-gray-100 border-2 border-gray-100 dark:border-gray-700 transform hover:scale-[1.02]'
                ]"
                :style="selfMessage ? 'border-radius: 1.5rem 1.5rem 0.375rem 1.5rem;' : 'border-radius: 1.5rem 1.5rem 1.5rem 0.375rem;'"
            >
                <!-- Effet de brillance -->
                <div 
                    v-if="selfMessage"
                    class="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 rounded-3xl pointer-events-none"
                ></div>

                <!-- Icône audio décorative -->
                <div class="flex items-center gap-3 mb-3">
                    <div 
                        :class="[
                            'w-11 h-11 rounded-2xl flex items-center justify-center shadow-lg',
                            selfMessage 
                                ? 'bg-white/20 backdrop-blur-md' 
                                : 'bg-gradient-to-br from-blue-500 to-purple-600'
                        ]"
                    >
                        <i 
                            :class="[
                                'las la-microphone text-xl',
                                selfMessage ? 'text-white' : 'text-white'
                            ]"
                        ></i>
                    </div>
                    <div class="flex-1">
                        <p 
                            :class="[
                                'text-xs font-semibold mb-0.5',
                                selfMessage ? 'text-white/90' : 'text-gray-600 dark:text-gray-400'
                            ]"
                        >
                            Message vocal
                        </p>
                        <div class="flex items-center gap-1.5">
                            <div 
                                :class="[
                                    'flex gap-0.5',
                                    selfMessage ? 'opacity-80' : 'opacity-60'
                                ]"
                            >
                                <div 
                                    v-for="i in 5" 
                                    :key="i"
                                    :class="[
                                        'w-0.5 rounded-full animate-wave',
                                        selfMessage ? 'bg-white' : 'bg-blue-500'
                                    ]"
                                    :style="`height: ${Math.random() * 12 + 8}px; animation-delay: ${i * 0.1}s`"
                                ></div>
                            </div>
                            <span 
                                :class="[
                                    'text-xs font-medium ml-1',
                                    selfMessage ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'
                                ]"
                            >
                                0:00
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Lecteur audio stylisé -->
                <div class="relative">
                    <audio 
                        controls 
                        :src="message?.content"
                        class="w-full h-10 rounded-xl"
                        style="outline: none;"
                    ></audio>
                </div>

                <!-- Footer avec timestamp -->
                <div 
                    :class="[
                        'flex items-center justify-between mt-3 pt-2 border-t',
                        selfMessage 
                            ? 'border-white/10' 
                            : 'border-gray-100 dark:border-gray-700'
                    ]"
                >
                    <div 
                        :class="[
                            'flex items-center gap-1.5 text-xs font-medium',
                            selfMessage ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'
                        ]"
                    >
                        <i class="las la-clock"></i>
                        <span>{{ hour }}</span>
                    </div>
                    
                    <!-- Status pour messages envoyés -->
                    <div v-if="selfMessage" class="flex items-center gap-1">
                        <i class="las la-check-double text-sm text-white/70"></i>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bouton répondre moderne -->
        <button
            @click="setParentReply"
            :class="[
                'opacity-0 group-hover:opacity-100 transition-all duration-300 flex-shrink-0 mb-2',
                'w-9 h-9 rounded-xl flex items-center justify-center',
                'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800',
                'hover:from-blue-500 hover:to-purple-600 dark:hover:from-blue-600 dark:hover:to-purple-700',
                'border border-gray-300 dark:border-gray-600 hover:border-transparent',
                'shadow-lg hover:shadow-xl',
                'transform hover:scale-110 active:scale-95',
                'group/reply',
                selfMessage ? 'order-first' : ''
            ]"
            title="Répondre"
        >
            <i class="fas fa-reply text-sm text-gray-600 dark:text-gray-300 group-hover/reply:text-white transition-colors duration-300"></i>
        </button>

    </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields';

export default {
    name: "AudioMessage",
    
    data() {
        return {
            audioPlayer: {
                isPlaying: false,
            },
            wave: {
                waveSuffer: null,
            },
            currentTime: '00:00',
            totalDuration: "00:00"
        }
    },

    props: {
        conversation: {
            type: Object,
            required: true
        },
        message: {
            type: Object,
            required: true
        },
        hour: {
            type: String,
            required: true
        },
        selfMessage: {
            type: Boolean,
            default() {
                return false
            }
        }
    },

    computed: {
        ...mapFields("messages/reply", {
            parentReply: "parentReply"
        })
    },

    methods: {
        setParentReply() {
            this.parentReply = this.message;
        },

        togglePlayPause() {
            this.wave.waveSuffer?.playPause();
            this.audioPlayer.isPlaying = !this.audioPlayer.isPlaying;
        },

        formatTime(seconds) { 
            return [seconds / 60, seconds % 60]
                .map((v) => `0${Math.floor(v)}`.slice(-2))
                .join(':');
        },

        timeUpdateHandler(time) {
            this.currentTime = this.formatTime(time);
        },

        readyHandler(duration) {
            this.totalDuration = this.formatTime(duration);
        }
    }
}
</script>

<style scoped>
/* Animations d'entrée */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-fadeIn {
    animation: fadeIn 0.4s ease-out;
}

/* Animation des ondes audio */
@keyframes wave {
    0%, 100% {
        transform: scaleY(1);
    }
    50% {
        transform: scaleY(1.5);
    }
}

.animate-wave {
    animation: wave 1s ease-in-out infinite;
}

/* Transitions pour les éléments conditionnels */
.slide-fade-enter-active {
    transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
    transform: translateX(-10px);
    opacity: 0;
}

.slide-fade-leave-to {
    transform: translateX(10px);
    opacity: 0;
}

.slide-down-enter-active {
    transition: all 0.3s ease-out;
}

.slide-down-leave-active {
    transition: all 0.2s ease-in;
}

.slide-down-enter-from {
    transform: translateY(-10px);
    opacity: 0;
}

.slide-down-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

/* Shadow personnalisée */
.shadow-3xl {
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Style du lecteur audio */
audio::-webkit-media-controls-panel {
    background-color: transparent;
}

audio::-webkit-media-controls-play-button,
audio::-webkit-media-controls-mute-button {
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
}

audio::-webkit-media-controls-current-time-display,
audio::-webkit-media-controls-time-remaining-display {
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}
</style>